/**
 * PharmacyDispensePrint — A4 print page for a pharmacy dispense / drug bill.
 * Route: /pharmacy/print/:id
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { PrintShell, PrintField, PrintSection, fmtDateTime, fmtINR } from '../components/print/PrintShell';

const PharmacyDispensePrint = () => {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get(`/pharmacy/dispenses/${id}`)
      .then(res => { if (mounted) setRecord(res.data); })
      .catch(() => { if (mounted) setRecord(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id]);

  const d = record || {};
  const items = Array.isArray(d.items) ? d.items : [];
  const statusColor = d.status === 'completed' ? '#10b981' : d.status === 'cancelled' ? '#ef4444' : '#f59e0b';

  return (
    <PrintShell
      title="Pharmacy Bill / Dispense Receipt"
      subtitle="Drug dispense receipt for patient pharmacy items."
      loading={loading}
      notFound={!loading && !record}
      hospitalId={d.hospital_id}
      docRef={d.dispense_no || `DSP-${(d.id || '').slice(0, 8).toUpperCase()}`}
      statusPill={{ label: d.status || 'completed', color: statusColor }}>
      <PrintSection title="Patient & Bill Details">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <PrintField label="Patient Name" value={d.patient_name} />
          <PrintField label="Bill Number" value={d.dispense_no} mono />
          <PrintField label="Dispensed By" value={d.dispensed_by_name} />
          <PrintField label="Dispensed On" value={fmtDateTime(d.created_at)} />
          {d.prescription_ref && <PrintField label="Prescription Ref" value={d.prescription_ref} mono />}
          {d.payment_mode && <PrintField label="Payment Mode" value={d.payment_mode} />}
        </div>
      </PrintSection>

      <PrintSection title="Dispensed Items">
        <table className="w-full text-xs border border-slate-200 rounded overflow-hidden">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-1.5 text-left">#</th>
              <th className="px-2 py-1.5 text-left">Drug</th>
              <th className="px-2 py-1.5 text-left">Batch</th>
              <th className="px-2 py-1.5 text-right">Qty</th>
              <th className="px-2 py-1.5 text-right">Unit Price</th>
              <th className="px-2 py-1.5 text-right">GST</th>
              <th className="px-2 py-1.5 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="px-2 py-3 text-center text-slate-400">No items.</td></tr>
            ) : items.map((it, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-2 py-1.5 text-slate-500">{i + 1}</td>
                <td className="px-2 py-1.5 font-medium">{it.drug_name || it.name}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{it.batch_no || '—'}</td>
                <td className="px-2 py-1.5 text-right">{it.quantity || it.qty}</td>
                <td className="px-2 py-1.5 text-right">{fmtINR(it.unit_price)}</td>
                <td className="px-2 py-1.5 text-right text-slate-500">{it.gst_pct ? `${it.gst_pct}%` : '—'}</td>
                <td className="px-2 py-1.5 text-right font-semibold">{fmtINR(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PrintSection>

      <PrintSection title="Bill Summary">
        <div className="ml-auto w-72 text-xs space-y-1">
          <div className="flex justify-between py-1"><span className="text-slate-500">Subtotal</span><span className="font-mono">{fmtINR(d.subtotal)}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-500">GST</span><span className="font-mono">{fmtINR(d.gst_amount)}</span></div>
          {(d.discount_amount || d.discount_pct) ? (
            <div className="flex justify-between py-1 text-rose-600"><span>Discount {d.discount_pct ? `(${d.discount_pct}%)` : ''}</span><span className="font-mono">- {fmtINR(d.discount_amount)}</span></div>
          ) : null}
          <div className="flex justify-between py-2 border-t border-slate-200 font-bold text-sm">
            <span>Grand Total</span><span className="font-mono">{fmtINR(d.grand_total)}</span>
          </div>
        </div>
      </PrintSection>

      {d.notes && (
        <PrintSection title="Notes">
          <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{d.notes}</p>
        </PrintSection>
      )}

      <div className="mt-8 pt-4 border-t border-slate-200 grid grid-cols-2 gap-12 text-xs">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Dispensed By</p>
          <p className="font-semibold">{d.dispensed_by_name || '—'}</p>
        </div>
        <div className="text-right">
          <div className="h-10 border-b border-slate-400 mb-2 inline-block w-40" />
          <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Patient / Receiver</p>
        </div>
      </div>
    </PrintShell>
  );
};

export default PharmacyDispensePrint;
